import type { Dispatch, SetStateAction } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "../../components/Toast";

type UserAgentOption = {
  value: string;
  label: string;
};

export function RobotsTxtGenerator() {
  const { t } = useTranslation();
  const [selectedAgent, setSelectedAgent] = useState("*");
  const [customAgent, setCustomAgent] = useState("");
  const [allowPaths, setAllowPaths] = useState<string[]>([""]);
  const [disallowPaths, setDisallowPaths] = useState<string[]>([""]);
  const [crawlDelay, setCrawlDelay] = useState("");
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const userAgents: UserAgentOption[] = [
    { value: "*", label: t("robotsGenerator.userAgents.all") },
    { value: "Googlebot", label: t("robotsGenerator.userAgents.google") },
    { value: "Bingbot", label: t("robotsGenerator.userAgents.bing") },
    { value: "DuckDuckBot", label: t("robotsGenerator.userAgents.duckduck") },
    { value: "YandexBot", label: t("robotsGenerator.userAgents.yandex") },
    { value: "Baiduspider", label: t("robotsGenerator.userAgents.baidu") },
    { value: "custom", label: t("robotsGenerator.userAgents.custom") },
  ];

  const resolvedAgent =
    selectedAgent === "custom" ? customAgent.trim() || "*" : selectedAgent;

  const robotsTxt = useMemo(() => {
    const allowEntries = allowPaths.map((value) => value.trim()).filter(Boolean);
    const disallowEntries = disallowPaths
      .map((value) => value.trim())
      .filter(Boolean);
    const lines = [`User-agent: ${resolvedAgent}`];

    if (allowEntries.length === 0 && disallowEntries.length === 0) {
      lines.push("Disallow:");
    } else {
      allowEntries.forEach((path) => lines.push(`Allow: ${path}`));
      disallowEntries.forEach((path) => lines.push(`Disallow: ${path}`));
    }

    const delayValue = Number.parseFloat(crawlDelay);
    if (Number.isFinite(delayValue) && delayValue >= 0) {
      lines.push(`Crawl-delay: ${delayValue}`);
    }

    const sitemap = sitemapUrl.trim();
    if (sitemap) {
      lines.push(`Sitemap: ${sitemap}`);
    }

    return lines.join("\n");
  }, [allowPaths, disallowPaths, crawlDelay, sitemapUrl, resolvedAgent]);

  const updatePath = (
    index: number,
    value: string,
    setter: Dispatch<SetStateAction<string[]>>
  ) => {
    setter((prev) => prev.map((item, idx) => (idx === index ? value : item)));
  };

  const addPath = (setter: Dispatch<SetStateAction<string[]>>) => {
    setter((prev) => [...prev, ""]);
  };

  const removePath = (
    index: number,
    setter: Dispatch<SetStateAction<string[]>>
  ) => {
    setter((prev) => {
      if (prev.length === 1) {
        return [""];
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(robotsTxt);
      setToastMessage(t("robotsGenerator.copied"));
    } catch {
      setToastMessage(t("robotsGenerator.copyFailed"));
    }
  };

  const downloadRobotsTxt = () => {
    const blob = new Blob([robotsTxt], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "robots.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="robots-generator">
      <div className="rg-grid">
        <div className="rg-card">
          <label className="rg-label" htmlFor="rg-agent">
            {t("robotsGenerator.userAgentLabel")}
          </label>
          <div className="rg-row">
            <select
              id="rg-agent"
              className="rg-select"
              value={selectedAgent}
              onChange={(event) => setSelectedAgent(event.target.value)}
            >
              {userAgents.map((agent) => (
                <option key={agent.value} value={agent.value}>
                  {agent.label}
                </option>
              ))}
            </select>
            {selectedAgent === "custom" && (
              <input
                className="rg-input"
                type="text"
                value={customAgent}
                onChange={(event) => setCustomAgent(event.target.value)}
                placeholder={t("robotsGenerator.customAgentPlaceholder")}
              />
            )}
          </div>
        </div>

        <div className="rg-card">
          <label className="rg-label" htmlFor="rg-delay">
            {t("robotsGenerator.crawlDelayLabel")}
          </label>
          <input
            id="rg-delay"
            className="rg-input"
            type="number"
            min={0}
            step={1}
            value={crawlDelay}
            onChange={(event) => setCrawlDelay(event.target.value)}
            placeholder={t("robotsGenerator.crawlDelayPlaceholder")}
          />
        </div>
      </div>

      <div className="rg-grid">
        <div className="rg-card">
          <div className="rg-card-header">
            <span className="rg-label">{t("robotsGenerator.allowLabel")}</span>
            <button
              type="button"
              className="rg-add-btn"
              onClick={() => addPath(setAllowPaths)}
            >
              {t("robotsGenerator.addAllow")}
            </button>
          </div>
          <div className="rg-list">
            {allowPaths.map((path, index) => (
              <div key={`allow-${index}`} className="rg-list-row">
                <input
                  className="rg-input"
                  type="text"
                  value={path}
                  onChange={(event) =>
                    updatePath(index, event.target.value, setAllowPaths)
                  }
                  placeholder={t("robotsGenerator.allowPlaceholder")}
                />
                <button
                  type="button"
                  className="rg-remove-btn"
                  onClick={() => removePath(index, setAllowPaths)}
                  aria-label={t("robotsGenerator.removePath")}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="rg-card">
          <div className="rg-card-header">
            <span className="rg-label">{t("robotsGenerator.disallowLabel")}</span>
            <button
              type="button"
              className="rg-add-btn"
              onClick={() => addPath(setDisallowPaths)}
            >
              {t("robotsGenerator.addDisallow")}
            </button>
          </div>
          <div className="rg-list">
            {disallowPaths.map((path, index) => (
              <div key={`disallow-${index}`} className="rg-list-row">
                <input
                  className="rg-input"
                  type="text"
                  value={path}
                  onChange={(event) =>
                    updatePath(index, event.target.value, setDisallowPaths)
                  }
                  placeholder={t("robotsGenerator.disallowPlaceholder")}
                />
                <button
                  type="button"
                  className="rg-remove-btn"
                  onClick={() => removePath(index, setDisallowPaths)}
                  aria-label={t("robotsGenerator.removePath")}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rg-card">
        <label className="rg-label" htmlFor="rg-sitemap">
          {t("robotsGenerator.sitemapLabel")}
        </label>
        <input
          id="rg-sitemap"
          className="rg-input"
          type="url"
          value={sitemapUrl}
          onChange={(event) => setSitemapUrl(event.target.value)}
          placeholder={t("robotsGenerator.sitemapPlaceholder")}
        />
      </div>

      <div className="rg-preview-card">
        <div className="rg-card-header">
          <span className="rg-label">{t("robotsGenerator.previewLabel")}</span>
          <div className="rg-actions">
            <button type="button" className="rg-secondary-btn" onClick={copyToClipboard}>
              {t("robotsGenerator.copy")}
            </button>
            <button
              type="button"
              className="rg-secondary-btn"
              onClick={downloadRobotsTxt}
            >
              {t("robotsGenerator.download")}
            </button>
          </div>
        </div>
        <pre className="rg-preview">{robotsTxt}</pre>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
